import React from 'react';
import { buildTree } from '../utils/whyWhyTree';

// Read-only renderer for an incident Why-Why Analysis tree.
//
// Indented tree with connector lines drawn by plain CSS borders (a vertical
// rule per child list + a short horizontal stub per node) — no diagram
// dependency. Root-cause nodes are highlighted green, matching the
// "Identified Root Cause" styling of the 5-Why modal.

const TreeNode = ({ node, depth }) => (
  <li className="relative pl-6">
    {/* horizontal connector stub from the parent's vertical rule */}
    <span className="absolute left-0 top-5 w-5 border-t-2 border-gray-300" aria-hidden="true" />
    <div
      className={`inline-block max-w-full rounded-lg border-2 px-3 py-2 my-1 text-sm ${
        node.is_root_cause
          ? 'bg-green-50 border-green-400 text-green-900'
          : 'bg-white border-gray-200 text-gray-800'
      }`}
      data-testid={`why-why-node-${node.id}`}
    >
      <span className="block text-xs font-bold text-gray-500 mb-0.5">
        Why? <span className="font-normal">(level {depth})</span>
        {node.is_root_cause && (
          <span className="ml-2 px-2 py-0.5 rounded-full bg-green-600 text-white text-[10px] font-bold uppercase tracking-wide">
            Root cause
          </span>
        )}
      </span>
      <span className="whitespace-pre-wrap break-words">{node.text || <em className="text-gray-400">(empty)</em>}</span>
    </div>
    {node.children.length > 0 && (
      <ul className="ml-3 border-l-2 border-gray-300">
        {node.children.map((child) => (
          <TreeNode key={child.id} node={child} depth={depth + 1} />
        ))}
      </ul>
    )}
  </li>
);

const WhyWhyTree = ({ problemStatement, nodes }) => {
  const roots = buildTree(nodes);
  return (
    <div className="overflow-x-auto" data-testid="why-why-tree">
      <div className="inline-block rounded-lg border-2 border-[#1B5C94] bg-blue-50 px-3 py-2 text-sm font-semibold text-[#1B5C94]">
        <span className="block text-xs font-bold text-gray-500 mb-0.5">Problem statement</span>
        <span className="whitespace-pre-wrap break-words">
          {problemStatement && problemStatement.trim() ? problemStatement : <em className="text-gray-400 font-normal">(not set)</em>}
        </span>
      </div>
      {roots.length > 0 ? (
        <ul className="ml-3 border-l-2 border-gray-300">
          {roots.map((root) => (
            <TreeNode key={root.id} node={root} depth={1} />
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-gray-500 italic">No "why" answers yet.</p>
      )}
    </div>
  );
};

export default WhyWhyTree;
